"use client";
/**
 * Habits' binding of the shared @owlcat/app-shell <AppSidebar>. Turns this app's
 * nav config (contexts/habits/navigation.ts) into the generic item shape —
 * localized labels + rendered icons — and supplies the footer profile cluster
 * (<AppHeaderControls>) + the next-intl <Link>/usePathname. All look + drawer
 * behavior lives in the package, so habits matches stories and every other app.
 */
import { useTranslations } from "next-intl";
import { Shield, ShieldCheck } from "lucide-react";
import { AppSidebar, AppSidebarFooter, AppHeaderControls, type SidebarNavItem, type AppHeaderControlsProps } from "@owlcat/app-shell";
import { Link, usePathname } from "@/lib/i18n/navigation";
import { habitsNavItems } from "@/contexts/habits/navigation";

const TIER_KEY: Record<string, string> = { free: "tierFree", basic: "tierBasic", pro: "tierPro", family: "tierFamily" };

export interface SidebarPlan {
  tier: string;
  /** the plan name from the accounts catalog (localized there); null → the app's own label */
  tierName?: string | null;
  isTop: boolean;
  billingUrl: string;
}

export function HabitsSidebar({
  brand,
  chrome,
  isAdmin = false,
  plan,
  legal,
}: {
  brand: { label: string; emoji: string; href: string };
  chrome: AppHeaderControlsProps;
  isAdmin?: boolean;
  plan?: SidebarPlan;
  /** accounts /info/privacy + /info/terms with this app's return — the fleet's legal row */
  legal: { privacyUrl: string; termsUrl: string };
}) {
  const t = useTranslations();
  const tPlan = useTranslations("habits.plan");
  const pathname = usePathname(); // locale-stripped → matches the locale-relative hrefs

  const items: SidebarNavItem[] = habitsNavItems.map((i) => {
    const Icon = i.icon;
    return {
      id: i.id,
      label: t(i.name),
      href: i.href,
      icon: <Icon className={i.secondary ? "h-4 w-4" : "h-5 w-5"} />,
      secondary: i.secondary,
    };
  });

  // Accounts admins get the admin section (secondary group) in every app.
  if (isAdmin) {
    items.push({
      id: "admin",
      label: t("habits.nav.admin"),
      href: "/admin",
      icon: <Shield className="h-4 w-4" />,
      secondary: true,
    });
    // the fleet admin (accounts: users, billing, subscriptions, comps) — the same entry in every app
    items.push({
      id: "accounts-admin",
      label: t("habits.nav.accountsAdmin"),
      href: `${(chrome.profile.accountsUrl ?? "").replace(/\/+$/, "")}/admin`,
      icon: <ShieldCheck className="h-4 w-4" />,
      secondary: true,
    });
  }

  // The FLEET footer (same as stories / film): [plan chip · profile / language / theme] → support → legal.
  const footer = (close: () => void) => (
    <AppSidebarFooter
      pill={plan ? {
        kind: "plan", appId: "habits", tierLabel: plan.tierName ?? tPlan(TIER_KEY[plan.tier] ?? "tierFree"), isTop: plan.isTop, isFree: plan.tier === "free",
        upgradeLabel: tPlan("upgrade"), manageLabel: tPlan("manage"), label: tPlan("title"), fallbackHref: plan.billingUrl,
      } : undefined}
      profile={<AppHeaderControls {...chrome} />}
      support={{ label: t("nav.support") }}
      legal={{ privacyUrl: legal.privacyUrl, termsUrl: legal.termsUrl, labels: { privacy: t("nav.privacy"), cookies: t("nav.cookies"), terms: t("nav.terms") } }}
      onNavigate={close}
    />
  );

  return <AppSidebar brand={brand} items={items} activePath={pathname} linkComponent={Link} footer={footer} collapsedFooter={<AppHeaderControls {...chrome} compact />} menuLabel={t("nav.menu")} />;
}
